export function parseCityStateZipFromSegment(segment) {
  if (!segment || typeof segment !== "string") return null;
  const s = segment.trim();
  const m = s.match(/^(.+)\s+([A-Za-z]{2})\s+(\d{5})(?:-(\d{4}))?$/);
  if (!m) return null;
  return {
    city: m[1].trim(),
    state: m[2].toUpperCase(),
    postal: m[4] ? `${m[3]}-${m[4]}` : m[3]
  };
}
export function parseAddressLine(line) {
  if (!line || typeof line !== "string") {
    return {
      street: "",
      city: "",
      state: "",
      postal: ""
    };
  }
  const trimmed = line.trim();
  const parts = trimmed.split(",").map(p => p.trim());
  const street = parts[0] || "";
  if (parts.length === 2 && street) {
    const csz = parseCityStateZipFromSegment(parts[1]);
    if (csz) {
      return {
        street,
        city: csz.city,
        state: csz.state,
        postal: csz.postal
      };
    }
    const stZip = parts[1].match(/^([A-Za-z]{2})\s+(\d{5})(?:-(\d{4}))?$/);
    if (stZip) {
      const blob = street;
      const tokens = blob.split(/\s+/).filter(Boolean);
      let splitStreet = "";
      let splitCity = "";
      if (tokens.length >= 2) {
        const lastTok = tokens[tokens.length - 1];
        if (/^[A-Za-z]{4,}$/.test(lastTok)) {
          splitStreet = tokens.slice(0, -1).join(" ");
          splitCity = lastTok;
        }
      }
      return {
        street: splitStreet,
        city: splitCity || blob,
        state: stZip[1].toUpperCase(),
        postal: stZip[3] ? `${stZip[2]}-${stZip[3]}` : stZip[2]
      };
    }
  }
  if (parts.length >= 3) {
    const lastSeg = parts[parts.length - 1];
    const cszTail = parseCityStateZipFromSegment(lastSeg);
    if (cszTail) {
      return {
        street: parts.slice(0, -1).join(", "),
        city: cszTail.city,
        state: cszTail.state,
        postal: cszTail.postal
      };
    }
  }
  const city = parts[1] || "";
  const stateZip = (parts[2] || "").trim();
  const stateZipParts = stateZip.split(/\s+/);
  const state = stateZipParts[0] || "";
  const postal = stateZipParts[1] || stateZipParts.slice(1).join(" ") || "";
  return {
    street,
    city,
    state,
    postal
  };
}
/** Collapse commas/spaces so "Denver CO 80202" matches "Denver, CO 80202". */
function normalizeCszCompare(s) {
  return String(s || "")
    .toUpperCase()
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function streetAlreadyIncludesCsz(street, city, state, zip) {
  const s = normalizeCszCompare(street);
  if (!s) return false;
  const lastComma = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  const lastSpace = [city, state, zip].filter(Boolean).join(" ");
  if (lastComma && s.includes(normalizeCszCompare(lastComma))) return true;
  if (lastSpace && s.includes(normalizeCszCompare(lastSpace))) return true;
  return false;
}

export function formatAddressPartsAsLine(p) {
  if (!p || typeof p !== "object") return "";
  const street = String(p.street ?? p.address ?? "").trim();
  const city = String(p.city ?? "").trim();
  const state = String(p.state ?? "").trim();
  const zip = String(p.zip ?? p.postal ?? "").trim();
  const last = [city, [state, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  // Woo (and some imports) historically stuffed CSZ into `address` while also
  // setting structured city/state/zip — don't append a second CSZ for validation.
  if (street && last && streetAlreadyIncludesCsz(street, city, state, zip)) return street;
  if (street && last) return `${street}, ${last}`;
  if (last) return last;
  return street;
}
export function formatSavedAddressBookLine(addr) {
  if (!addr || typeof addr !== "object") return "";
  const streetPart = [addr.address, addr.apt].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  const city = String(addr.city ?? "").trim();
  const state = String(addr.state ?? "").trim();
  const zip = String(addr.zip ?? "").trim();
  const lastLine = [city, state, zip].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  if (streetPart && lastLine) return `${streetPart}, ${lastLine}`;
  if (lastLine) return lastLine;
  return streetPart;
}

/** Shipping form: street (+ apt) + structured city/state/zip → single line for validation / display. */
export function formatShippingFormSideAddressLine(form, side = "from") {
  const pre = side === "to" ? "to" : "from";
  if (!form || typeof form !== "object") return "";
  const street = String(form[`${pre}Address`] ?? "").trim();
  const apt = String(form[`${pre}Apt`] ?? "").trim();
  const city = String(form[`${pre}City`] ?? "").trim();
  const state = String(form[`${pre}State`] ?? "").trim();
  const zip = String(form[`${pre}Zip`] ?? "").trim();
  if (city || state || zip) {
    // Avoid duplicating CSZ when Address was already filled as a full one-liner (legacy locked fill / Woo).
    if (streetAlreadyIncludesCsz(street, city, state, zip)) {
      return [street, apt].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
    }
    return formatSavedAddressBookLine({
      address: street,
      apt,
      city,
      state,
      zip,
    });
  }
  // Legacy: address field may already be a full single-line string.
  return street;
}
export function formatSavedAddressDropdownDetailLine(addr) {
  if (!addr || typeof addr !== "object") return "";
  const line = formatSavedAddressBookLine(addr).trim();
  const phone = addr.phone != null ? String(addr.phone).trim() : "";
  const email = addr.email != null ? String(addr.email).trim() : "";
  const tail = [phone, email].filter(Boolean).join(" • ");
  if (!line) return tail;
  if (!tail) return line;
  return `${line} • ${tail}`;
}
export function normalizeOrderDetailAddressFields(raw) {
  const o = raw && typeof raw === "object" && raw !== null ? raw : {};
  const line = typeof raw === "string" ? raw : String(o.address || "");
  const cityIn = String(o.city || "").trim();
  const stateIn = String(o.state || "").trim().slice(0, 2);
  const zipIn = String(o.zip || "").trim();
  if (cityIn || stateIn || zipIn) {
    let address = String(o.address != null ? o.address : line).trim();
    // Legacy Woo lines: "1455 Market St, Denver CO 80202" + structured CSZ.
    if (address.includes(",") && streetAlreadyIncludesCsz(address, cityIn, stateIn, zipIn)) {
      const p = parseAddressLine(address);
      if (p.street) address = p.street;
    }
    return {
      address,
      apartment: String(o.apartment || "").trim(),
      city: cityIn,
      state: stateIn,
      zip: zipIn
    };
  }
  const p = parseAddressLine(String(line));
  return {
    address: (p.street || line).trim(),
    apartment: String(o.apartment || "").trim(),
    city: p.city || "",
    state: p.state || "",
    zip: p.postal || ""
  };
}
